/* 

  Usage:

    Add the attribute data-animation-type to the element 
    that you want to animate with one of the animation types
      animation types:
        fade-in
        clip-lines
        reveal
      examples:
        data-animation-type="clip-lines"
        data-animation-type="reveal"

    Optionally you can customize timing with the 
    attributes data-animation-delay or data-animation-duration
    the value must be in miliseconds and without specifying unit
      examples:
        data-animation-delay="240"
        data-animation-delay="{{ 180 | times: forloop.index0 }}"
        data-animation-duration="450"

*/
(() => {
  /** @type {NodeListOf<HTMLElement>} */
  const sectionsToAnimate = document.querySelectorAll("section.shopify-section:has([data-animation-type])");
  if (sectionsToAnimate.length === 0) {
    return;
  }

  const globalOptions = {
    // trigger animations when scrolled 36% into the screen
    // add extra bounding on sides for marquee/ticker
    rootMargin: "0% 300% -36% 300%",
  };

  // 2 ways to go from here, either have observers ready to go before hand with anfunc
  // or define callback funcs and invoke a generic observer each time passing in global options
  // not sure if for any of the cases we might want custom options?
  const fadeInObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      for (const elementToAnimate of entry.target.querySelectorAll('[data-animation-type^="fade-in"]')) {
        elementToAnimate.toggleAttribute("animate", entry.isIntersecting || entry.boundingClientRect.top < 0);
      }
    }
  }, globalOptions);

  const clipLinesObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      /** @type {NodeListOf<HTMLElement>} */
      const elementsWithClipLinesAnim = entry.target.querySelectorAll('[data-animation-type^="clip-lines"]');
      for (const elementToAnimate of elementsWithClipLinesAnim) {
        let baseDelay = Number(elementToAnimate.dataset.animationDelay) || 0;
        /** @type {NodeListOf<HTMLElement>} */
        const innerSplittedLines = elementToAnimate.querySelectorAll(".splitted-line");
        for (const line of innerSplittedLines) {
          line.style.transitionDelay = `${baseDelay}ms`;
          baseDelay += 180;
          elementToAnimate.toggleAttribute("animate", entry.isIntersecting || entry.boundingClientRect.top < 0);
        }
      }
    }
  }, globalOptions);

  const revealObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      for (const elementToAnimate of entry.target.querySelectorAll('[data-animation-type^="reveal"]')) {
        elementToAnimate.toggleAttribute("animate", entry.isIntersecting || entry.boundingClientRect.top < 0);
      }
    }
  }, globalOptions);

  /**
   * @param {HTMLElement} textElement
   */
  function splitVisibleLines(textElement) {
    // we're gonna have to use the zero-width space trick
    // if any of the zero-width helpers is further down then we know where a new line would be
    const zeroWidthSpaceChar = "&#8203;";
    /** @type {string[]} */
    let tempWordsArray;

    if (textElement.dataset.encodedText !== undefined) {
      tempWordsArray = decodeURI(textElement.dataset.encodedText).split(" ");
    } else {
      const originalInnerHTML = textElement.innerHTML
        .replaceAll("\t", "")
        .replaceAll("\n", "")
        .replaceAll("<br", " <br")
        .replaceAll("\u2028", " <br>"); // without newlinechars or tabs and add space to html breaklines
      textElement.dataset.encodedText = encodeURI(originalInnerHTML);
      tempWordsArray = originalInnerHTML.split(" ");
    }

    const wordsWithoutSpaces = tempWordsArray.filter((/** @type {String} */ word) => word.length > 0);
    const wordsWithZeroWidthHelpers = [];

    let tempInnerHTML = "";

    // iterate through each letter in word, make decisions then append helper to end of word
    for (const word of wordsWithoutSpaces) {
      // if we're 100% sure we're not inside any tags
      // example: "health"
      if (word[0] !== "<" && word[word.length - 1] !== ">") {
        wordsWithZeroWidthHelpers.push(`${word}<span class="zero-width-helper">${zeroWidthSpaceChar}</span>`);
      } else if (word[0] === "<" && word.indexOf("/") !== 1 && word[word.length - 1] === ">") {
        // if we find two < and the second one is followed by a /
        if (
          word.split("").filter((/** @type {String} */ char) => char === "<").length > 1 &&
          word.split("").filter((/** @type {String} */ char) => char === "<").length ===
            word.split("").filter((/** @type {String} */ char) => char === ">").length
        ) {
          // example: "<i>akkermansia</i>"
          wordsWithZeroWidthHelpers.push(`${word}<span class="zero-width-helper">${zeroWidthSpaceChar}</span>`);
        } else {
          // example: "<p>"
          wordsWithZeroWidthHelpers.push(
            `<span class="zero-width-helper not-word">${zeroWidthSpaceChar}</span>${word}`,
          );
        }
      } else if (word[0] === "<" && word.indexOf(">") !== -1 && word.indexOf(">") < word.length) {
        if (word[word.indexOf("<") + 1] === "/") {
          // example: "</i>Unlike"
          wordsWithZeroWidthHelpers.push(`${word}<span class="zero-width-helper">${zeroWidthSpaceChar}</span>`);
        } else {
          // example: "<strong>Title"
          wordsWithZeroWidthHelpers.push(`${word}<span class="zero-width-helper">${zeroWidthSpaceChar}</span>`);
        }
      } else if (word[word.length - 1] === ">" && word.indexOf("<") > 0) {
        if (word[word.indexOf("<") + 1] === "/") {
          // example: "now</p>"
          const tempWord = word.replace("<", `<span class="zero-width-helper">${zeroWidthSpaceChar}</span><`);
          wordsWithZeroWidthHelpers.push(tempWord);
        } else {
          // example: "next-<strong>"
          const tempWord = word.replace("<", `<span class="zero-width-helper">${zeroWidthSpaceChar}</span><`);
          wordsWithZeroWidthHelpers.push(tempWord);
        }
      }
    }

    // temporarily show the new helpers to see where they end up
    tempInnerHTML = wordsWithZeroWidthHelpers.join(" ");
    textElement.innerHTML = tempInnerHTML;

    // calculate and order/map words per line
    /** @type {Array.<Array.<string>>} */
    const wordsPerLineArray = [];
    /** @type {Array.<string>} */
    const linesArray = [];
    let lineCount = 0;

    // each helper's index corresponds to a word in our original wordsWithoutSpaces array
    /** @type {NodeListOf<HTMLElement>} */
    const allZeroWidthHelpers = textElement.querySelectorAll(".zero-width-helper");
    allZeroWidthHelpers.forEach(
      (
        /** @type {HTMLElement} */ zeroWidthHelper,
        /** @type {Number} */ index,
        /** @type {NodeListOf<HTMLElement>} */ helpersArray,
      ) => {
        if (index === 0) {
          // initialize the first line, push the first word into the first line
          wordsPerLineArray.push([]);
          wordsPerLineArray[lineCount].push(wordsWithoutSpaces[index]);
          linesArray.push("");
          linesArray[lineCount] += wordsWithoutSpaces[index];
        } else {
          if (zeroWidthHelper.offsetTop === helpersArray[index - 1].offsetTop) {
            // if has the same vertical offset as previous word, push to the same line
            wordsPerLineArray[lineCount].push(wordsWithoutSpaces[index]);
            linesArray[lineCount] += ` ${wordsWithoutSpaces[index]}`;
          } else if (zeroWidthHelper.offsetTop > helpersArray[index - 1].offsetTop) {
            // if has more vertical offset than previous word, create a new line and push to it
            wordsPerLineArray.push([]);
            linesArray.push("");
            lineCount += 1;
            wordsPerLineArray[lineCount].push(wordsWithoutSpaces[index]);
            linesArray[lineCount] += wordsWithoutSpaces[index];
          }
        }
      },
    );

    // remove helpers
    for (const zeroWidthHelper of allZeroWidthHelpers) {
      zeroWidthHelper.remove();
    }

    // guess where to add the wrappers for each line
    // but first sanitize them
    const sanitizedLines = [];
    let bufferedOpenTag = "<>";
    let bufferedClosedTag = "</>";
    const standaloneOpeningTagRegex = /[^>.]<[^/]([a-z]+)(?![^>]*\/>)[^>]*>[^<.]/;
    const anyClosingTagRegex = /[^>.]<\/([a-z]+)(?![^>]*\/>)[^>]*>/;
    let tagStillOpen = false;

    for (const lineString of linesArray) {
      let tempLine = "";
      /** @type {Array.<string> | null} */
      const matchAgainstOpeningTag = lineString.match(standaloneOpeningTagRegex);

      // if this is the line that closes the tag, prepend its opening tag
      if (tagStillOpen && lineString.indexOf(bufferedClosedTag) !== -1) {
        // example: "Akkermansia</em> is a powerful strain</p>"
        tagStillOpen = false;
        if (lineString.indexOf("<br") === 0) {
          tempLine =
            lineString.substring(0, lineString.indexOf(">") + 1) +
            bufferedOpenTag +
            lineString.substring(lineString.indexOf(">") + 1);
        } else {
          tempLine = bufferedOpenTag + lineString;
        }
        sanitizedLines.push(tempLine);
      }
      // if the tag is still open and this line still doesn't close it
      else if (tagStillOpen && lineString.indexOf(bufferedClosedTag) === -1) {
        // example: "Akkermansia muciniphila, Clostridium Butyricum"
        tempLine = bufferedOpenTag + lineString + bufferedClosedTag;
        sanitizedLines.push(tempLine);
      } else if (
        matchAgainstOpeningTag !== null &&
        lineString.match(anyClosingTagRegex) === null &&
        matchAgainstOpeningTag[0].indexOf("<br") === -1
      ) {
        // if a non-brkline tag is opened on this line and not closed on this same line
        tagStillOpen = true;
        // save the matched regex
        bufferedOpenTag = matchAgainstOpeningTag[0].slice(1, -1);
        if (bufferedOpenTag.indexOf(" ") !== -1) {
          bufferedClosedTag = bufferedOpenTag
            .substring(0, bufferedOpenTag.indexOf(" "))
            .replace(" ", ">")
            .replace("<", "</");
        } else {
          // what would be its corresponding closing tag
          bufferedClosedTag = bufferedOpenTag.replace("<", "</");
        }

        // example: "<p>Discover strains like <em>Akkermansia "
        tempLine = lineString + bufferedClosedTag;
        sanitizedLines.push(tempLine);
      } else {
        // nothing to do, just append the line
        sanitizedLines.push(lineString);
      }
    }

    const linesWithSpans = [];

    for (const lineString of sanitizedLines) {
      let tempLine = "";

      // begins
      if (lineString[0] === "<") {
        tempLine += lineString.replace(">", `><span class="splitted-line-wrapper"><span class="splitted-line">`);
      } else {
        tempLine += `<span class="splitted-line-wrapper"><span class="splitted-line">${lineString}`;
      }

      // ends
      if (lineString[lineString.length - 1] === ">") {
        tempLine = `${tempLine.substring(0, tempLine.lastIndexOf("<"))}</span></span>${tempLine.substring(tempLine.lastIndexOf("<"))}`;
      } else {
        tempLine += "</span></span>";
      }

      linesWithSpans.push(tempLine);
    }

    // replace content with wrapped lines
    tempInnerHTML = linesWithSpans.join(" ");
    textElement.innerHTML = tempInnerHTML;
  }

  /** @type {Array.<HTMLElement>} */
  const elementsToCalcOnResize = [];

  // initially I was going to make it based on each element intersection but based on the video most trigger with the parent container
  // sort between animation types
  for (const section of sectionsToAnimate) {
    const animationTypesInSection = {
      fadeIn: false,
      clipLines: false,
      reveal: false,
    };

    /** @type {NodeListOf<HTMLElement>} */
    const elementsToAnimateInSection = section.querySelectorAll("[data-animation-type]");
    for (const elementToAnimate of elementsToAnimateInSection) {
      switch (elementToAnimate.dataset.animationType) {
        case "fade-in":
        case "fade-in static":
        case "fade-in bottom": {
          if (elementToAnimate.dataset.animationDelay !== undefined) {
            elementToAnimate.style.transitionDelay = `${elementToAnimate.dataset.animationDelay}ms`;
          }
          if (elementToAnimate.dataset.animationDuration !== undefined) {
            elementToAnimate.style.transitionDuration = `${elementToAnimate.dataset.animationDuration}ms`;
          }
          animationTypesInSection.fadeIn = true;
          break;
        }

        case "clip-lines":
        case "clip-lines bottom": {
          splitVisibleLines(elementToAnimate);
          animationTypesInSection.clipLines = true;
          elementsToCalcOnResize.push(elementToAnimate);
          break;
        }

        case "reveal": {
          if (elementToAnimate.dataset.animationDelay !== undefined) {
            elementToAnimate.style.transitionDelay = `${elementToAnimate.dataset.animationDelay}ms`;
          }
          if (elementToAnimate.dataset.animationDuration !== undefined) {
            elementToAnimate.style.transitionDuration = `${elementToAnimate.dataset.animationDuration}ms`;
          }
          animationTypesInSection.reveal = true;
          break;
        }

        default: {
          break;
        }
      }
    }

    if (animationTypesInSection.fadeIn) {
      fadeInObserver.observe(section);
    }

    if (animationTypesInSection.clipLines) {
      clipLinesObserver.observe(section);
    }

    if (animationTypesInSection.reveal) {
      revealObserver.observe(section);
    }

    if (Object.values(animationTypesInSection).includes(true)) {
      section.setAttribute("data-has-animations", "true");
    }
  }

  if (elementsToCalcOnResize.length > 0) {
    let baseWidth = document.body.clientWidth;
    /** @type {number | NodeJS.Timeout | undefined | null} */
    let delayCalc;

    const resizeObserver = new ResizeObserver((entries) => {
      if (baseWidth !== entries[0].borderBoxSize[0].inlineSize) {
        if (delayCalc) {
          clearTimeout(delayCalc);
        }
        delayCalc = setTimeout(() => {
          for (const elementToCalcOnResize of elementsToCalcOnResize) {
            splitVisibleLines(elementToCalcOnResize);
          }
          baseWidth = entries[0].borderBoxSize[0].inlineSize;
        }, 80);
      }
    });

    resizeObserver.observe(document.body);
  }
})();
